import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin } from "lucide-react";
import { api, money, compact } from "@/lib/api";
import InvestPanel from "@/components/invest/InvestPanel";
import MobileInvestBar from "@/components/invest/MobileInvestBar";
import { FALLBACK_IMG } from "@/components/site/PropertyCard";

const Stat = ({ label, value, accent }) => (
  <div className="rounded-xl border border-line bg-obsidian/50 p-5">
    <p className="eyebrow">{label}</p>
    <p className={`mt-2 font-mono text-xl tracking-tight ${accent ? "text-amber-500" : "text-white"}`}>{value}</p>
  </div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const [prop, setProp] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProp(data)).catch(() => setError(true));
  }, [id]);
  useEffect(load, [load]);

  if (error) return <div className="container-x py-24 text-slate-400" data-testid="property-not-found">This property is no longer listed. <Link to="/properties" className="text-amber-500">Back to properties</Link></div>;
  if (!prop) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  return (
    <div className="container-x py-14" data-testid="property-detail-page">
      <Link to="/properties" className="-my-2 inline-flex items-center gap-2 py-2 text-sm text-slate-400 hover:text-white" data-testid="property-back-link"><ArrowLeft className="h-4 w-4" /> All properties</Link>
      <div className="mt-8 overflow-hidden rounded-3xl border border-line">
        <img src={prop.image_url || FALLBACK_IMG} alt={prop.title} className="aspect-[4/3] w-full object-cover sm:aspect-[21/9]" />
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="eyebrow text-amber-500">{prop.property_type}</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl" data-testid="property-detail-title">{prop.title}</h1>
          <p className="mt-3 flex items-center gap-2 text-slate-400"><MapPin className="h-4 w-4" />{prop.location}</p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Valuation" value={compact(prop.valuation)} />
            <Stat label="Per share" value={money(prop.share_price, 0)} />
            <Stat label="Yield" value={`${prop.rental_yield}%`} accent />
            <Stat label="Available" value={`${prop.available_shares}/${prop.total_shares}`} />
          </div>
          <div className="panel mt-8 p-6 sm:p-8">
            <p className="eyebrow mb-4">About this property</p>
            <p className="whitespace-pre-line leading-relaxed text-slate-300" data-testid="property-detail-description">{prop.description || "No description yet."}</p>
          </div>
        </div>
        <div id="invest-panel" className="scroll-mt-24 lg:col-span-5">
          <InvestPanel assetType="property" asset={prop} price={prop.share_price} label={prop.title} maxBuy={prop.available_shares} onDone={load} />
        </div>
      </div>
      <MobileInvestBar label={prop.title} price={`${money(prop.share_price, 0)} / share · ${prop.rental_yield}% yield`} tone="amber" />
    </div>
  );
}
